import React from 'react'
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import PropTypes from 'prop-types'

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
  },
  header: {
    marginBottom: 15,
    borderBottomWidth: 2,
    paddingBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  date: {
    fontSize: 9,
    textAlign: 'right',
  },
  cardInfo: {
    marginBottom: 20,
    padding: 15,
    borderRadius: 6,
    borderWidth: 1,
  },
  cardInfoTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  cardInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cardInfoItem: {
    width: '50%',
    marginBottom: 8,
    paddingRight: 10,
  },
  cardInfoLabel: {
    fontSize: 9,
    marginBottom: 2,
    fontWeight: 'bold',
  },
  cardInfoValue: {
    fontSize: 10,
    fontWeight: 'normal',
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
  },
  tableBodyRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    minHeight: 25,
  },
  tableCol: {
    padding: 5,
    justifyContent: 'center',
    borderRightWidth: 1,
  },
  dateCol: {
    width: '12%',
  },
  typeCol: {
    width: '12%',
  },
  descriptionCol: {
    width: '36%',
  },
  authNumberCol: {
    width: '10%',
  },
  merchantIdCol: {
    width: '15%',
  },
  amountCol: {
    width: '15%',
  },
  tableColLast: {
    borderRightWidth: 0,
  },
  tableHeaderText: {
    fontWeight: 'bold',
    fontSize: 10,
    letterSpacing: 0.5,
  },
  tableBodyText: {
    fontSize: 9,
    lineHeight: 1.4,
  },
  tableBodyTextAmount: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  tableBodyTextDate: {
    fontSize: 9,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    borderTopWidth: 1,
    paddingTop: 8,
  },
})

const formatValue = (value, type) => {
  if (value === null || value === undefined || value === '') return ''
  if (type === 'datetime') {
    const dateString = String(value)
    if (dateString.includes(' ')) {
      const parts = dateString.split(' ')
      return parts.join('\n')
    }
  }
  return String(value)
}

const getColumnType = (columnValue) => {
  switch (columnValue) {
    case 'SHOWDATE':
      return 'datetime'
    case 'MONEYAMOUNT':
      return 'amount'
    default:
      return 'text'
  }
}

const getColumnStyle = (column, isLast) => {
  const baseStyle = [styles.tableCol]
  switch (column.VALUE) {
    case 'SHOWDATE':
      baseStyle.push(styles.dateCol)
      break
    case 'TRANSACTIONTYPELABEL':
      baseStyle.push(styles.typeCol)
      break
    case 'TRANSACTIONDESCRIPTION':
      baseStyle.push(styles.descriptionCol)
      break
    case 'AUTHNUMBER':
      baseStyle.push(styles.authNumberCol)
      break
    case 'MERCHANTID':
      baseStyle.push(styles.merchantIdCol)
      break
    case 'MONEYAMOUNT':
      baseStyle.push(styles.amountCol)
      break
    default:
      baseStyle.push({ width: '15%' })
  }
  if (isLast) {
    baseStyle.push(styles.tableColLast)
  }
  return baseStyle
}

const formatCardNumber = (cardNumber) => cardNumber || 'N/A'

const formatExpiryDate = (expiryDate) => {
  if (!expiryDate) return 'N/A'
  if (typeof expiryDate === 'string') {
    try {
      const date = new Date(expiryDate)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear()).slice(-2)
      return `${month}/${year}`
    } catch {
      return expiryDate
    }
  }
  return String(expiryDate)
}

const TransactionTablePrintDetails = ({
  transactions = [],
  columns = [],
  cardInfo = {},
  title = 'Transaction Statement',
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.date}>
          Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
        </Text>
      </View>
      {cardInfo && Object.keys(cardInfo).length > 0 && (
        <View style={styles.cardInfo}>
          <Text style={styles.cardInfoTitle}>Card Information</Text>
          <View style={styles.cardInfoGrid}>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>Tracking Number</Text>
              <Text style={styles.cardInfoValue}>{formatCardNumber(cardInfo.TRACKINGNUMBER)}</Text>
            </View>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>Campaign Name</Text>
              <Text style={styles.cardInfoValue}>{cardInfo.CAMPAIGNNAME ? cardInfo.CAMPAIGNNAME.trim() : 'N/A'}</Text>
            </View>
            <View style={styles.cardInfoItem}>
              <Text style={styles.cardInfoLabel}>Card Expiry Date</Text>
              <Text style={styles.cardInfoValue}>{formatExpiryDate(cardInfo.EXPIRYDATE)}</Text>
            </View>
          </View>
        </View>
      )}
      <View style={styles.table}>
        <View style={styles.tableHeaderRow}>
          {columns.map((col) => (
            <View style={getColumnStyle(col, col === columns[columns.length - 1])} key={col.VALUE}>
              <Text style={styles.tableHeaderText}>{col.LABEL}</Text>
            </View>
          ))}
        </View>
        {transactions.map((transaction, rowIdx) => {
          const transactionKey = transaction.ID || transaction.AUTHNUMBER || `row-${rowIdx}`
          return (
            <View
              style={[styles.tableBodyRow, rowIdx % 2 === 0 ? styles.tableBodyRowEven : styles.tableBodyRowOdd]}
              key={transactionKey}
            >
              {columns.map((col) => {
                const columnType = getColumnType(col.VALUE)
                const cellKey = `${transactionKey}-${col.VALUE}`
                let textStyle
                if (columnType === 'amount') {
                  textStyle = styles.tableBodyTextAmount
                } else if (columnType === 'datetime') {
                  textStyle = styles.tableBodyTextDate
                } else {
                  textStyle = styles.tableBodyText
                }
                return (
                  <View style={getColumnStyle(col, col === columns[columns.length - 1])} key={cellKey}>
                    <Text style={textStyle}>
                      {formatValue(transaction[col.VALUE], columnType)}
                    </Text>
                  </View>
                )
              })}
            </View>
          )
        })}
      </View>
    </Page>
  </Document>
)

TransactionTablePrintDetails.propTypes = {
  transactions: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      LABEL: PropTypes.string.isRequired,
      VALUE: PropTypes.string.isRequired,
    })
  ),
  cardInfo: PropTypes.object,
  title: PropTypes.string,
}

export default TransactionTablePrintDetails
